import React from 'react';
import { Card } from 'react-bootstrap';
import './StepPalette.css';
import {
  FaDatabase,
  FaPlay,
  FaEdit,
  FaPlus,
  FaTrash,
  FaArrowRight,
  FaQuestion,
  FaStop,
  FaFileImport,
  FaFileExport,
  FaCode,
  FaEnvelope,
  FaSearch,
  FaTable,
} from 'react-icons/fa';
import { MdLoop } from 'react-icons/md';
import { BiGitBranch } from 'react-icons/bi';
import { VscVariableGroup } from 'react-icons/vsc';
import { AiOutlineFunction } from 'react-icons/ai';

export const stepTypes = [
  {
    type: 'executeRunbook',
    label: 'Execute Runbook',
    description: 'Execute another runbook',
    icon: <FaPlay />,
    action: 0,
    color: '#10b981',
  },
  {
    type: 'query',
    label: 'Query',
    description: 'Query data from a system',
    icon: <FaDatabase />,
    action: 1,
    color: '#3b82f6',
  },
  {
    type: 'lookup',
    label: 'Lookup',
    description: 'Lookup specific record',
    icon: <FaSearch />,
    action: 2,
    color: '#06b6d4',
  },
  {
    type: 'create',
    label: 'Create',
    description: 'Create new record',
    icon: <FaPlus />,
    action: 3,
    color: '#10b981',
  },
  {
    type: 'update',
    label: 'Update',
    description: 'Update existing record',
    icon: <FaEdit />,
    action: 4,
    color: '#f59e0b',
  },
  {
    type: 'delete',
    label: 'Delete',
    description: 'Delete record',
    icon: <FaTrash />,
    action: 5,
    color: '#ef4444',
  },
  {
    type: 'ifElse',
    label: 'If/Else',
    description: 'Branch based on condition',
    icon: <BiGitBranch />,
    action: 6,
    color: '#f59e0b',
  },
  {
    type: 'forEach',
    label: 'For Each',
    description: 'Iterate through dataset',
    icon: <MdLoop />,
    action: 8,
    color: '#8b5cf6',
  },
  {
    type: 'setVar',
    label: 'Set Variable',
    description: 'Set variable value',
    icon: <FaCode />,
    action: 10,
    color: '#6366f1',
  },
  {
    type: 'jsonSetVar',
    label: 'JSON Set Variable',
    description: 'Set variable value from JSON',
    icon: <FaCode />,
    action: 24,
    color: '#6366f1',
  },
  {
    type: 'getPushedData',
    label: 'Get Pushed Data',
    description: 'Get data from external source',
    icon: <FaFileImport />,
    action: 11,
    color: '#0ea5e9',
  },
  {
    type: 'executeStoredProcedure',
    label: 'Execute Stored Procedure',
    description: 'Run stored procedure',
    icon: <AiOutlineFunction />,
    action: 12,
    color: '#8b5cf6',
  },
  {
    type: 'declareVar',
    label: 'Declare Variable',
    description: 'Declare new variable',
    icon: <VscVariableGroup />,
    action: 14,
    color: '#6366f1',
  },
  {
    type: 'logMessage',
    label: 'Log Message',
    description: 'Log message to output',
    icon: <FaFileExport />,
    action: 15,
    color: '#64748b',
  },
  {
    type: 'setError',
    label: 'Set Error',
    description: 'Set error state',
    icon: <FaStop />,
    action: 16,
    color: '#ef4444',
  },
  {
    type: 'endLoop',
    label: 'End Loop',
    description: 'End loop iteration',
    icon: <FaStop />,
    action: 17,
    color: '#8b5cf6',
  },
  {
    type: 'sendEmail',
    label: 'Send Email',
    description: 'Send email notification',
    icon: <FaEnvelope />,
    action: 18,
    color: '#3b82f6',
  },
  {
    type: 'upsertClientVar',
    label: 'Upsert Client Variable',
    description: 'Update or insert client variable',
    icon: <VscVariableGroup />,
    action: 19,
    color: '#8b5cf6',
  },
  {
    type: 'buildTableCache',
    label: 'Build Table Cache',
    description: 'Build cache for table data',
    icon: <FaTable />,
    action: 20,
    color: '#10b981',
  },
  {
    type: 'lookupTableCache',
    label: 'Lookup Table Cache',
    description: 'Lookup data in table cache',
    icon: <FaTable />,
    action: 21,
    color: '#06b6d4',
  },
  {
    type: 'queryTableCache',
    label: 'Query Table Cache',
    description: 'Query cached table data',
    icon: <FaTable />,
    action: 22,
    color: '#3b82f6',
  },
  {
    type: 'deleteTableCache',
    label: 'Delete Table Cache',
    description: 'Delete cached table data',
    icon: <FaTable />,
    action: 23,
    color: '#ef4444',
  },
];

const stepGroups = [
  {
    name: 'CRUD Operations',
    steps: ['query', 'lookup', 'create', 'update', 'delete'],
  },
  {
    name: 'Control Flow',
    steps: ['ifElse', 'forEach', 'endLoop'],
  },
  {
    name: 'Variables',
    steps: ['declareVar', 'setVar', 'jsonSetVar', 'upsertClientVar'],
  },
  {
    name: 'Data Operations',
    steps: ['getPushedData', 'executeStoredProcedure', 'buildTableCache', 'lookupTableCache', 'queryTableCache', 'deleteTableCache'],
  },
  {
    name: 'Actions',
    steps: ['executeRunbook', 'sendEmail', 'logMessage', 'setError'],
  },
];

// Palette tile ICONS use the brand teal; the names stay black (see
// .wf-palette-label). Node color means CONNECTOR once a step is configured, so
// the palette doesn't pre-color per step type.
const PALETTE_ITEM_COLOR = '#007681';

function StepPalette({ onAddNode }) {
  return (
    <Card>
      <Card.Header className="bg-gradient-primary text-white">
        <h6 className="mb-0">Step Palette</h6>
      </Card.Header>
      <Card.Body className="p-2" style={{ maxHeight: '700px', overflowY: 'auto' }}>
        {stepGroups.map((group) => (
          <div key={group.name} className="mb-2">
            <div className="text-muted small fw-bold px-2 py-1 text-uppercase" style={{ fontSize: '0.65rem' }}>
              {group.name}
            </div>
            <div className="wf-palette-grid">
              {group.steps.map((stepTypeKey) => {
                const stepType = stepTypes.find(s => s.type === stepTypeKey);
                if (!stepType) return null;
                return (
                  <button
                    key={stepType.type}
                    type="button"
                    className="wf-palette-tile"
                    onClick={() => onAddNode(stepType)}
                    title={`${stepType.label}${stepType.description ? ' — ' + stepType.description : ''}`}
                  >
                    <span className="wf-palette-icon" style={{ color: PALETTE_ITEM_COLOR }}>
                      {stepType.icon}
                    </span>
                    <span className="wf-palette-label">{stepType.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </Card.Body>
    </Card>
  );
}

export default StepPalette;
